import { reactive } from 'vue'
import EXIF from 'exif-js'
import Croppie from 'croppie'
import { httpGetJson, httpPatchJson, httpDeleteJson } from '@/lib/net'
import pubsub from '@/lib/pubsub'
import { compressImage } from '@/lib/photos'
import {
  requestCloudinarySignature,
  uploadToCloudinary,
} from '@/services/photo-service'
import { log } from '@/services/logging-service'

export interface UserProfile {
  account_id?: string | null
  email?: string | null
  facebook_id?: string | null
  google_id?: string | null
  inserted_at?: string | null
  updated_at?: string | null
  first_name?: string | null
  last_name?: string | null
  picture_url?: string | null
  roles?: string[] | null
}

export interface PublicProfile {
  account_id?: string | null
  first_name?: string | null
  last_name?: string | null
  name?: string | null
  photo_url?: string | null
}

const state = reactive<{
  profiles: Record<string, PublicProfile>
  currentUser: UserProfile | null
}>({
  profiles: {},
  currentUser: null,
})

/**
 * Look up a profile by ID. Will pull from the state store if available.
 *
 * @param id - Account ID to look up the profile for
 * @return Profile object
 */
export const getProfile = async (
  id: string
): Promise<PublicProfile | undefined> => {
  if (!id) {
    return
  }

  // look in state store
  if (state.profiles[id]) {
    return state.profiles[id]
  }
  // if not in state store look it up from the api and then store it
  try {
    const profile = await httpGetJson<PublicProfile>(
      `/v1/public/profiles/${id}`
    )
    if (profile?.name) {
      state.profiles[id] = profile
    }
    return profile
  } catch (e) {
    log('get_profile_failed', {
      error: e,
      profile_id: id,
    })
  }
}

/**
 * Look up current user's profile. Will pull from the state store if available.
 *
 * @param options - configurable options
 * @param options.forceRefresh - Skip cached state and fetch profile from API
 * @return Profile object
 * @throws on request failure
 */
export const getCurrentUserProfile = async ({
  forceRefresh = false,
} = {}): Promise<UserProfile> => {
  if (!forceRefresh && state.currentUser) {
    return state.currentUser
  }

  try {
    const profile = await httpGetJson<UserProfile>('/v1/profile')
    if (profile?.email) {
      state.currentUser = profile
    }
    return profile
  } catch (e) {
    log('get_current_user_profile_failed', { error: e })
    throw e
  }
}

/**
 * Update current user's profile
 *
 * @param fields - Profile fields to update
 */
export const updateCurrentUserProfile = async (
  fields: UserProfile
): Promise<void> => {
  if (!fields.first_name || fields.first_name.length < 2) {
    throw new Error('Removal of name is no longer allowed')
  }

  const body = {
    email: fields.email,
    first_name: fields.first_name,
    last_name: fields.last_name,
  }

  try {
    await httpPatchJson('/v1/profile', body)

    if (state.currentUser) {
      state.currentUser.email = body.email
      state.currentUser.first_name = body.first_name
      state.currentUser.last_name = body.last_name
    }
  } catch (e) {
    log('update_current_user_profile_failed', { error: e })
    throw e
  }
}

/**
 * Get latest user profile picture url. Replaces "v1"
 * with "v{timestamp}" in picture url.
 *
 * Set the location version as a timestamp to break
 * the browser cache and ensure the most current photo is
 * being downloaded.
 *
 * @param pictureUrl - user's profile picture url
 * @return latest timestamped profile picture url
 */
export const getLatestUserProfilePictureUrl = (pictureUrl: string): string => {
  return pictureUrl.replace('v1', 'v' + new Date().getTime())
}

/**
 * Delete existing profile photo for account
 */
export const deleteProfilePhoto = async (): Promise<void> => {
  await httpDeleteJson('/v1/profile/photo', undefined)
  pubsub.publish('updatedProfilePic', {
    imageURL: '',
  })
}

/**
 * Upload a new profile pic to Cloudinary.
 *
 * @param image - image blob to upload as the new profile pic
 */
export const uploadProfilePhoto = async (image: Blob | File): Promise<void> => {
  const compressedImage = await compressImage(image)
  const signature = await requestCloudinarySignature()
  const response = await uploadToCloudinary(signature, compressedImage)
  pubsub.publish('updatedProfilePic', {
    imageURL: response?.secure_url,
  })
}

/**
 * Adds a photo cropping tool to the profile image before uploading
 *
 * @param imageElement - image element to load the cropping tool on
 * @return New instance of cropping tool for the profile photo
 */
export const cropProfilePhoto = (imageElement: HTMLElement): Croppie => {
  // croppie is expecting EXIF to be available globally
  window.EXIF = EXIF
  return new Croppie(imageElement, {
    enableExif: true,
    enforceBoundary: true,
    showZoomer: false,
    viewport: {
      type: 'circle',
      width: 250,
      height: 250,
    },
    boundary: {
      width: 290,
      height: 300,
    },
  })
}

/**
 * Finalize the cropped profile photo and upload it
 *
 * @param cropTool - Instance of croppie to pull image from
 */
export const setFinalCroppedImage = async (
  cropTool: Croppie
): Promise<void> => {
  // Get the cropped image, not cropped circular, and send to Cloudinary
  const croppedImage = await cropTool.result({
    type: 'blob',
    circle: false,
  })
  uploadProfilePhoto(croppedImage)
}
